import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { loginLocal, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;

  const goAfterAuth = (role) => {
    if (role === "admin") return navigate("/admin");
    if (from?.pathname) return navigate(from.pathname, { state: from.state });
    navigate("/my-errah");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await loginLocal(form.email, form.password);
        toast.success("Welcome back");
        goAfterAuth(u.role);
      } else {
        await signup(form);
        toast.success("Account created");
        goAfterAuth("customer");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    if (from?.pathname) {
      try { sessionStorage.setItem("errah_post_login", JSON.stringify(from)); } catch (_) {}
    }
    const redirectUrl = window.location.origin + "/my-errah";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-5" data-testid="login-page">
      <Seo title="My ERRAH — Sign in" description="Sign in to your ERRAH account." />
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6"><img src="/errah-logo.png" alt="ERRAH — Style Your Space" className="h-28 w-auto object-contain" /></Link>

        <div className="flex mb-8 border border-white/10">
          {["login", "signup"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`tab-${m}`}
              className={`flex-1 py-3 text-sm tracking-wide transition-colors ${mode === m ? "bg-[#C9A45C] text-black font-semibold" : "text-gray-400"}`}>
              {m === "login" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "signup" && (
            <input required placeholder="Full name" value={form.name} data-testid="login-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          )}
          <input required type="email" placeholder="Email" value={form.email} data-testid="login-email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          {mode === "signup" && (
            <input placeholder="Mobile (optional)" value={form.phone} data-testid="login-phone"
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          )}
          <input required type="password" placeholder="Password" value={form.password} data-testid="login-password"
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          <button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full bg-[#C9A45C] text-black py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-gray-600">OR</span><div className="flex-1 h-px bg-white/10" /></div>

        <button onClick={googleLogin} data-testid="login-google"
          className="w-full border border-white/15 py-3.5 text-sm hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-3">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4 24 4 16 4 9.1 8.6 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.6-11.3-8.4l-6.5 5C9.1 39.4 16 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.9 35.6 44 30.2 44 24c0-1.3-.1-2.3-.4-3.5z" /></svg>
          Continue with Google
        </button>

        <p className="mt-8 text-center text-xs text-gray-600">
          Are you a partner? <Link to="/partner/login" className="text-[#C9A45C] hover:underline">Partner login</Link>
        </p>
      </div>
    </div>
  );
}
